import React, { useCallback, useMemo } from 'react';

import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { EventInfo, EventInfoWithoutPlayers, isTeamEvent } from '@/api/events/types/types';
import { EventCard } from '@/components/EventCard';
import { getFinalScoreColumns } from '@/feature/bets-sbk/utils/utils';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { useEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { useFormatMatchDetails } from '@/feature/event-details-sbk/hooks/use-format-match-details';
import { Event, EventDetails } from '@/feature/event-details-sbk/types';
import { useSinglePlayerProfile } from '@/feature/fantasy-event-sheets/SinglePlayerProfileProvider';
import { useFantasyEventsTabsStore } from '@/feature/fantasy-event-sheets/hooks/use-fantasy-events-tabs-store';
import { useEventDetail } from '@/hooks/use-sbk-event-details-cache';
import { RootStackParamList } from '@/navigation/types';
import { isWeb } from '@/utils/constants-platform-specific';
import { gameUtils } from '@/utils/games';
import { isPGA } from '@/utils/league';

import { PGAEventCard } from './PGAEventCard';

type PickemEventCardProps = {
    pickemEvent: EventInfo | EventInfoWithoutPlayers;
    index: number;
    variant: 'lobby' | 'eventDetails';
    isSelected?: boolean;
};

/**
 * fetches detailed event info and conditionally renders a PAM-connected or "simple" card.
 * - PAM-connected - we have a PAM event ID, so we can fetch event score and clock and subscribe to updates
 * - simple - we only have basic event info from Pickem BE
 * this card is used in the lobby events list, and it requests all the event info from Pickem BE
 * so we can load the event details sheet smoothly
 */
export const PickemEventLobbyCard = ({ pickemEvent, index, variant }: PickemEventCardProps) => {
    const pamEventId = pickemEvent?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id;

    const eventTime = gameUtils.createTimeLabel(pickemEvent);

    return pamEventId ? (
        <PickemEventCardWithPam pickemEvent={pickemEvent} index={index} pamEventId={pamEventId} variant={variant} />
    ) : (
        <PickemEventCardSimple pickemEvent={pickemEvent} index={index} eventTitle={eventTime} variant={variant} />
    );
};

export const PickemEventDetailsCard = ({ pickemEvent, index, variant, isSelected }: PickemEventCardProps) => {
    const pamEventId = pickemEvent?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id;

    const eventTime = gameUtils.createTimeLabel(pickemEvent);

    return pamEventId ? (
        <PickemEventCardWithPam
            pickemEvent={pickemEvent}
            index={index}
            pamEventId={pamEventId}
            variant={variant}
            isSelected={isSelected}
        />
    ) : (
        <PickemEventCardSimple
            pickemEvent={pickemEvent}
            index={index}
            eventTitle={eventTime}
            variant={variant}
            isSelected={isSelected}
        />
    );
};

/**
 * this card is used for events that are not connected to PAM
 * most of the information displayed is using the data from the Pickem BE
 * optionally, you can send the final score and event title to display it on the card
 * this is used by PAM-connected cards to display the final score
 */

const PickemEventCardSimple = ({
    pickemEvent,
    eventTitle,
    awayScore,
    homeScore,
    variant,
    isSelected,
    index,
}: PickemEventCardProps & { eventTitle: string; awayScore?: number; homeScore?: number }) => {
    const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
    const { isPlayerProfileModalOpen, closeSinglePlayerProfile } = useSinglePlayerProfile();

    const navigateToEventDetailSheet = useCallback(() => {
        useFantasyEventsTabsStore.getState().actions.setSelectedTab(index);

        if (isPlayerProfileModalOpen?.value) {
            closeSinglePlayerProfile?.();
        }

        if (isWeb) {
            navigation.setParams({
                eventId: pickemEvent.id,
            });
        }
        if (variant === 'lobby') {
            navigation.navigate('FantasyEventsSheets', {
                league: pickemEvent.league,
                eventId: pickemEvent.id || '',
                contextKey: new Date().getTime().toString(),
            });
        }
    }, [
        closeSinglePlayerProfile,
        index,
        isPlayerProfileModalOpen?.value,
        navigation,
        pickemEvent.id,
        pickemEvent.league,
        variant,
    ]);

    if (isPGA(pickemEvent.league)) {
        return <PGAEventCard event={pickemEvent} onPress={navigateToEventDetailSheet} isSelected={isSelected} />;
    }

    if (!isTeamEvent(pickemEvent)) {
        return null;
    }

    const teams = eventUtils.getTeams(pickemEvent);

    if (!teams[0] || !teams[1]) {
        return null;
    }

    return (
        <EventCard
            onPress={navigateToEventDetailSheet}
            variant={variant}
            awayTeamIcon={teams[0].icon}
            awayTeamShortCode={teams[0].name}
            homeTeamIcon={teams[1].icon}
            homeTeamShortCode={teams[1].name}
            isLive={gameUtils.isLive(pickemEvent)}
            eventTitle={eventTitle}
            league={pickemEvent.league}
            awayPrimaryTeamColor={teams[0].color}
            homePrimaryTeamColor={teams[1].color}
            awaySecondaryTeamColor={teams[0].secondaryColor}
            homeSecondaryTeamColor={teams[1].secondaryColor}
            awayScore={awayScore}
            homeScore={homeScore}
            isSelected={isSelected}
        />
    );
};

/**
 * this card is used for events that are connected to PAM
 * it fetches the event details and conditionally renders a PAM-connected
 * if we have errors fetching the event, we display a simple card
 * if the event is live, we subscribe to updates and display the score
 */
const PickemEventCardWithPam = ({
    pickemEvent,
    index,
    pamEventId,
    variant,
    isSelected,
}: PickemEventCardProps & { pamEventId: string }) => {
    //do not make PAM API calls if we know that the event is not LIVE from the pickem API data
    const { data: event, error } = useEvent(pamEventId, gameUtils.isLive(pickemEvent));

    const eventDetails = useEventDetail(event?.id ?? '') ?? event?.event_details;

    if (event && !error && eventDetails.status?.toUpperCase() === 'LIVE') {
        return (
            <PickemEventCardWithScore
                variant={variant}
                event={event}
                pickemEvent={pickemEvent}
                index={index}
                eventDetails={eventDetails}
                isSelected={isSelected}
            />
        );
    }

    const eventTitle = gameUtils.createTimeLabel(pickemEvent);

    return (
        <PickemEventCardSimple
            variant={variant}
            pickemEvent={pickemEvent}
            index={index}
            eventTitle={eventTitle}
            isSelected={isSelected}
        />
    );
};

/**
 * this card is used for events that are connected to PAM, are live
 * and we can subscribe to updates
 */
const PickemEventCardWithScore = ({
    pickemEvent,
    index,
    event,
    eventDetails,
    variant,
    isSelected,
}: PickemEventCardProps & {
    event: Event;
    eventDetails: EventDetails;
}) => {
    const finalScores = useMemo(
        () => (event && eventDetails ? getFinalScoreColumns(eventDetails, event.sport)[0] : undefined),
        [event, eventDetails]
    );

    const matchDetails = useFormatMatchDetails(event);

    const eventTitle = gameUtils.createTimeLabel(pickemEvent);

    return (
        <PickemEventCardSimple
            variant={variant}
            index={index}
            pickemEvent={pickemEvent}
            eventTitle={matchDetails || eventTitle}
            awayScore={finalScores?.awayScore}
            homeScore={finalScores?.homeScore}
            isSelected={isSelected}
        />
    );
};
